import { EMPTY_INPUT, type MatchEvent, type MatchState } from "../game/simulation";
import { MatchSocket, spectatorSocketUrl } from "../network/MatchSocket";
import { LocalMatchDriver } from "../network/LocalMatchDriver";
import { fetchActiveMatches, type ActiveMatchSummary } from "../network/adminApi";
import { ArenaRenderer } from "../phaser/createArenaRenderer";
import { toArenaViewModel } from "../phaser/viewModel";
import { LocalPuzzleDriver } from "../puzzle/network/LocalPuzzleDriver";
import {
  PuzzleMatchSocket,
  puzzleSpectatorSocketUrl,
} from "../puzzle/network/PuzzleMatchSocket";
import { PuzzleRenderer } from "../puzzle/phaser/createPuzzleRenderer";
import { summarizePuzzleEvent, toPuzzleViewModel } from "../puzzle/phaser/viewModel";
import type { PuzzleEvent, PuzzleMatchState } from "../puzzle/simulation";

interface AdminSession {
  id: string;
  gameKind: "arena" | "puzzle";
  dispose: () => void;
}

export class UnifiedAdminApp {
  private readonly sessions = new Map<string, AdminSession>();
  private refreshTimer: number | null = null;
  private destroyed = false;

  constructor(private readonly root: HTMLElement) {}

  mount(): void {
    this.root.className = "unified-admin";
    this.root.innerHTML = `
      <main class="ua-shell">
        <header class="ua-header">
          <div>
            <p>READ-ONLY · 2 SECOND DELAY</p>
            <h1>LIVE MATCH CONTROL</h1>
          </div>
          <nav>
            <span id="ua-feed" data-state="syncing"><i></i>SYNCING</span>
            <button id="ua-refresh" type="button">새로고침</button>
            <a href="/">FOAM POP</a>
            <a href="/arena">FOAM ARENA</a>
          </nav>
        </header>
        <section id="ua-grid" class="ua-grid" aria-live="polite">
          <div class="ua-empty"><i></i><strong>경기 피드를 불러오는 중</strong></div>
        </section>
      </main>
    `;
    this.required<HTMLButtonElement>("#ua-refresh").addEventListener("click", () => void this.refresh(true));
    void this.refresh(true);
    this.refreshTimer = window.setInterval(() => void this.refresh(false), 8_000);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.refreshTimer !== null) window.clearInterval(this.refreshTimer);
    this.refreshTimer = null;
    this.clearSessions();
    this.root.replaceChildren();
    this.root.className = "";
  }

  private async refresh(force: boolean): Promise<void> {
    if (this.destroyed) return;
    let matches: ActiveMatchSummary[];
    let preview = false;
    try {
      matches = await fetchActiveMatches();
      this.setFeed("LIVE INDEX", "live");
    } catch (error) {
      if (!isLocalHost()) {
        this.setFeed("FEED ERROR", "error");
        this.clearSessions();
        this.showEmpty(error instanceof Error ? error.message : "경기 목록을 불러올 수 없습니다.");
        return;
      }
      matches = previewMatches();
      preview = true;
      this.setFeed("LOCAL PREVIEW", "preview");
    }
    if (matches.length === 0 && isLocalHost()) {
      matches = previewMatches();
      preview = true;
      this.setFeed("LOCAL PREVIEW", "preview");
    }
    const nextKey = matches.map((match) => `${match.id}:${match.gameKind}`).join("|");
    const currentKey = [...this.sessions.values()]
      .map((session) => `${session.id}:${session.gameKind}`)
      .join("|");
    if (!force && nextKey === currentKey) return;

    this.clearSessions();
    const grid = this.required<HTMLElement>("#ua-grid");
    grid.replaceChildren();
    if (matches.length === 0) {
      this.showEmpty("현재 진행 중인 경기가 없습니다.");
      return;
    }
    for (const match of matches) this.createCard(match, preview);
  }

  private createCard(summary: ActiveMatchSummary, preview: boolean): void {
    const card = document.createElement("article");
    card.className = "ua-card";
    card.dataset.kind = summary.gameKind;
    card.innerHTML = `
      <header>
        <div><span data-code></span><strong data-title></strong></div>
        <div class="ua-score"><b data-blue>0</b><span>:</span><b data-orange>0</b></div>
        <button data-focus type="button">전체 화면 ↗</button>
      </header>
      <div class="ua-canvas"></div>
      <aside>
        <span>EVENT LOG</span>
        <ol data-events><li class="is-empty">주요 이벤트 대기 중</li></ol>
      </aside>
      <footer>
        <span data-round>ROUND 1</span>
        <span data-time>-- SEC</span>
        <span data-connection data-state="${preview ? "preview" : "connecting"}"><i></i>${preview ? "LOCAL PREVIEW" : "CONNECTING"}</span>
      </footer>
    `;
    this.requiredWithin<HTMLElement>(card, "[data-code]").textContent = summary.id;
    this.requiredWithin<HTMLElement>(card, "[data-title]").textContent = summary.gameKind === "puzzle"
      ? `FOAM POP · ${summary.mode}`
      : `FOAM ARENA · ${summary.mode}`;
    this.required<HTMLElement>("#ua-grid").append(card);
    this.requiredWithin<HTMLButtonElement>(card, "[data-focus]").addEventListener("click", () => {
      void this.toggleFocus(card);
    });

    const dispose = summary.gameKind === "puzzle"
      ? this.startPuzzleCard(card, summary, preview)
      : this.startArenaCard(card, summary, preview);
    this.sessions.set(summary.id, { id: summary.id, gameKind: summary.gameKind, dispose });
  }

  private startPuzzleCard(card: HTMLElement, summary: ActiveMatchSummary, preview: boolean): () => void {
    const renderer = new PuzzleRenderer({
      parent: this.requiredWithin<HTMLElement>(card, ".ua-canvas"),
      compact: true,
    });
    if (preview) {
      const driver = new LocalPuzzleDriver({
        mode: summary.mode === "2v2" ? "team" : "duel",
        readHumanInput: () => ({ aim: 0, fire: false }),
        automateHuman: true,
        onSnapshot: (state) => {
          renderer.publish(state);
          this.updatePuzzleStats(card, state);
        },
        onEvents: (events) => {
          renderer.publishEvents(events);
          this.appendPuzzleEvents(card, events);
        },
      });
      driver.start();
      return () => {
        driver.destroy();
        renderer.destroy();
      };
    }

    const socket = new PuzzleMatchSocket({
      url: puzzleSpectatorSocketUrl(summary.id),
      spectator: true,
      onSnapshot: (state) => {
        renderer.publish(state);
        this.updatePuzzleStats(card, state);
      },
      onEvents: (events) => {
        renderer.publishEvents(events);
        this.appendPuzzleEvents(card, events);
      },
      onStatus: (status, latency) => this.updateConnection(card, status, latency),
      onError: (message) => this.updateConnection(card, "error", undefined, message),
    });
    socket.connect();
    return () => {
      socket.close();
      renderer.destroy();
    };
  }

  private startArenaCard(card: HTMLElement, summary: ActiveMatchSummary, preview: boolean): () => void {
    const renderer = new ArenaRenderer({
      parent: this.requiredWithin<HTMLElement>(card, ".ua-canvas"),
      compact: true,
    });
    if (preview) {
      const driver = new LocalMatchDriver({
        mode: summary.mode === "2v2" ? "team" : "duel",
        readHumanInput: () => ({ ...EMPTY_INPUT }),
        automateHuman: true,
        onSnapshot: (state) => {
          renderer.publish(state);
          this.updateArenaStats(card, state);
        },
        onEvents: (events) => this.appendArenaEvents(card, events),
      });
      driver.start();
      return () => {
        driver.stop();
        renderer.destroy();
      };
    }

    const socket = new MatchSocket({
      url: spectatorSocketUrl(summary.id),
      spectator: true,
      onSnapshot: (state) => {
        renderer.publish(state);
        this.updateArenaStats(card, state);
      },
      onEvents: (events) => this.appendArenaEvents(card, events),
      onStatus: (status, latency) => this.updateConnection(card, status, latency),
      onError: (message) => this.updateConnection(card, "error", undefined, message),
    });
    socket.connect();
    return () => {
      socket.close();
      renderer.destroy();
    };
  }

  private updatePuzzleStats(card: HTMLElement, state: PuzzleMatchState): void {
    const view = toPuzzleViewModel(state);
    this.setScopedText(card, "[data-blue]", String(view.scores.blue));
    this.setScopedText(card, "[data-orange]", String(view.scores.orange));
    this.setScopedText(card, "[data-round]", `ROUND ${view.round} · ${view.phase.toUpperCase()}`);
    this.setScopedText(card, "[data-time]", view.overtime ? "OVERTIME" : `${Math.ceil(view.roundTimeSeconds)} SEC`);
  }

  private updateArenaStats(card: HTMLElement, state: MatchState): void {
    const view = toArenaViewModel(state);
    this.setScopedText(card, "[data-blue]", String(view.score.A));
    this.setScopedText(card, "[data-orange]", String(view.score.B));
    this.setScopedText(card, "[data-round]", `ROUND ${view.round} · ${view.phase.toUpperCase()}`);
    this.setScopedText(card, "[data-time]", `${Math.ceil(view.roundTimeSeconds)} SEC`);
  }

  private appendPuzzleEvents(card: HTMLElement, events: PuzzleEvent[]): void {
    for (const event of events) {
      const summary = summarizePuzzleEvent(event);
      if (summary) this.prependEvent(card, `T${event.tick}`, summary.label, summary.tone);
    }
  }

  private appendArenaEvents(card: HTMLElement, events: MatchEvent[]): void {
    for (const event of events) {
      const label = event.type === "captured" ? "CAPTURE"
        : event.type === "rescued" ? "ALLY RESCUE"
        : event.type === "popped" ? `POP · ${event.team.toUpperCase()}`
        : event.type === "golden_pop_started" ? "GOLDEN POP"
        : event.type === "round_ended" ? `${event.winner.toUpperCase()} ROUND`
        : event.type === "match_ended" ? `${event.winner.toUpperCase()} WINS`
        : null;
      if (label) this.prependEvent(card, `T${event.tick}`, label, event.type);
    }
  }

  private prependEvent(card: HTMLElement, tick: string, label: string, tone: string): void {
    const log = this.requiredWithin<HTMLOListElement>(card, "[data-events]");
    log.querySelector(".is-empty")?.remove();
    const item = document.createElement("li");
    item.dataset.tone = tone;
    const small = document.createElement("small");
    small.textContent = tick;
    const strong = document.createElement("strong");
    strong.textContent = label;
    item.append(small, strong);
    log.prepend(item);
    while (log.children.length > 6) log.lastElementChild?.remove();
  }

  private updateConnection(
    card: HTMLElement,
    status: string,
    latency?: number,
    errorMessage?: string,
  ): void {
    const node = this.requiredWithin<HTMLElement>(card, "[data-connection]");
    node.dataset.state = status;
    node.lastChild?.remove();
    node.append(errorMessage ?? (status === "live"
      ? `LIVE${latency === undefined ? "" : ` · ${Math.round(latency)}MS`}`
      : status.toUpperCase()));
  }

  private async toggleFocus(card: HTMLElement): Promise<void> {
    const grid = this.required<HTMLElement>("#ua-grid");
    const focused = !card.classList.contains("is-focused");
    for (const candidate of grid.querySelectorAll<HTMLElement>(".ua-card")) {
      candidate.classList.toggle("is-focused", candidate === card && focused);
    }
    grid.classList.toggle("has-focus", focused);
    const button = this.requiredWithin<HTMLButtonElement>(card, "[data-focus]");
    button.textContent = focused ? "분할 화면으로 ↙" : "전체 화면 ↗";
    if (focused && document.fullscreenEnabled) {
      await card.requestFullscreen({ navigationUI: "hide" }).catch(() => undefined);
    } else if (!focused && document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
    }
  }

  private clearSessions(): void {
    for (const session of this.sessions.values()) session.dispose();
    this.sessions.clear();
  }

  private showEmpty(message: string): void {
    const empty = document.createElement("div");
    empty.className = "ua-empty";
    const strong = document.createElement("strong");
    strong.textContent = message;
    const small = document.createElement("small");
    small.textContent = "경기가 시작되면 이 화면에 자동으로 표시됩니다.";
    empty.append(strong, small);
    this.required<HTMLElement>("#ua-grid").replaceChildren(empty);
  }

  private setFeed(label: string, state: string): void {
    const feed = this.required<HTMLElement>("#ua-feed");
    feed.dataset.state = state;
    feed.lastChild?.remove();
    feed.append(label);
  }

  private setScopedText(parent: HTMLElement, selector: string, value: string): void {
    const node = parent.querySelector<HTMLElement>(selector);
    if (node) node.textContent = value;
  }

  private required<T extends Element>(selector: string): T {
    const node = this.root.querySelector<T>(selector);
    if (!node) throw new Error(`Admin element missing: ${selector}`);
    return node;
  }

  private requiredWithin<T extends Element>(parent: ParentNode, selector: string): T {
    const node = parent.querySelector<T>(selector);
    if (!node) throw new Error(`Admin card element missing: ${selector}`);
    return node;
  }
}

function previewMatches(): ActiveMatchSummary[] {
  return [
    { id: "POP-01 · DEMO", gameKind: "puzzle", mode: "1v1", phase: "playing", playerCount: 2 },
    { id: "ARENA-02 · DEMO", gameKind: "arena", mode: "2v2", phase: "playing", playerCount: 4 },
  ];
}

function isLocalHost(): boolean {
  const hostname = location.hostname;
  return hostname === "127.0.0.1" ||
    hostname === "localhost" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    /^172\.(?:1[6-9]|2\d|3[01])\./u.test(hostname);
}
